import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Star, ShieldCheck, Briefcase } from 'lucide-react';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components';
import { getReviewForm, submitReview } from '../services/reviewsApi';
import type { ReviewFormData, ReviewQuestion } from '../services/reviewsApi';

/**
 * Publieke review-wizard (/review/:token, geen login) — Belagom Review Engine.
 *
 * Drie sterrenvragen (Vakkennis, Snelheid & Bereikbaarheid, Doelgerichtheid per discipline) plus
 * één open vraag. Dezelfde pagina bedient de anonieme medewerker en de
 * HR-projectleider; de backend bepaalt via `reviewer_rol` welke vraag 3 en
 * welke privacytekst hoort.
 */

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

const Bedankt: React.FC<{ titel: string; tekst: string }> = ({ titel, tekst }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
        <Star className="w-8 h-8 text-green-600 fill-green-600" aria-hidden="true" />
      </div>
      <h1 className="text-xl font-semibold text-gray-900 mb-2">{titel}</h1>
      <p className="text-gray-600">{tekst}</p>
    </div>
  </div>
);

const SterrenVraag: React.FC<{
  vraag: ReviewQuestion;
  nummer: number;
  waarde: number;
  onChange: (v: number) => void;
}> = ({ vraag, nummer, waarde, onChange }) => {
  const { t } = useTranslation('professional');
  const [hover, setHover] = useState(0);
  const actief = hover || waarde;
  return (
    <fieldset>
      <legend className="text-sm font-medium text-gray-500 mb-1">
        {nummer}. {vraag.label}
      </legend>
      <p className="text-gray-900 mb-3">{vraag.question}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 w-full sm:w-auto">{vraag.lowLabel}</span>
        <div className="flex gap-1" role="radiogroup" aria-label={vraag.label} onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={waarde === v}
              onClick={() => onChange(v)}
              onMouseEnter={() => setHover(v)}
              className="p-1 rounded hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B1F3A]"
              aria-label={t('review.starsAriaLabel', { count: v })}
            >
              <Star className={`w-8 h-8 transition-colors ${actief >= v ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} aria-hidden="true" />
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500 w-full sm:w-auto">{vraag.highLabel}</span>
      </div>
    </fieldset>
  );
};

export const DossierReviewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('professional');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState<ReviewFormData | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [openComment, setOpenComment] = useState('');

  useEffect(() => {
    if (!token) {
      setError(t('review.invalidLink'));
      setLoading(false);
      return;
    }
    getReviewForm(token)
      .then((res) => {
        if (res.already_submitted) setAlreadySubmitted(true);
        else if (res.data) setForm(res.data);
      })
      .catch((err) => setError(foutMelding(err, t('review.loadError'))))
      .finally(() => setLoading(false));
  }, [token, t]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !form) return;
    const hasScores = Object.values(scores).some((v) => v >= 1 && v <= 5);
    if (!hasScores && !openComment.trim()) {
      setError(t('review.fillAtLeastOne'));
      return;
    }
    setError(null);
    setSubmitting(true);
    submitReview({
      token,
      q1: scores.q1,
      q2: scores.q2,
      q3: scores.q3,
      open_comment: openComment.trim() || undefined,
    })
      .then(() => setSuccess(true))
      .catch((err) => setError(foutMelding(err, t('review.submitError'))))
      .finally(() => setSubmitting(false));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <p className="text-gray-600">{t('review.loading')}</p>
      </div>
    );
  }

  if (alreadySubmitted) return <Bedankt titel={t('review.alreadySubmittedTitle')} tekst={t('review.alreadySubmittedDesc')} />;
  if (success) return <Bedankt titel={t('review.successTitle')} tekst={t('review.successDesc')} />;

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <p className="text-red-600" role="alert">{error ?? t('review.loadError')}</p>
        </div>
      </div>
    );
  }

  const isHr = form.reviewer_rol === 'employer';
  const open = form.open_question;

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-5 sm:p-8">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {isHr ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-[#0B1F3A] border border-blue-100 px-2 py-0.5 text-xs font-medium">
                <Briefcase className="w-3.5 h-3.5" aria-hidden="true" />
                {form.bron_label ?? t('review.titleEmployer')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 text-green-800 border border-green-100 px-2 py-0.5 text-xs font-medium">
                <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
                {t('review.anonymousBadge')}
              </span>
            )}
            <span className="text-xs text-gray-500">{t('review.duration')}</span>
          </div>

          <h1 className="text-2xl font-semibold text-gray-900 mb-1">
            {isHr ? t('review.titleEmployer') : t('review.title')}
          </h1>
          <p className="text-gray-600 mb-2">
            {isHr
              ? t('review.subtitleEmployer', { name: form.professional_name })
              : t('review.subtitle', { name: form.professional_name })}
          </p>
          <p className="text-sm text-gray-500 mb-8">{isHr ? t('review.employerNote') : t('review.anonymousNote')}</p>

          <form onSubmit={handleSubmit} className="space-y-8">
            {form.questions.map((q, i) => (
              <SterrenVraag
                key={q.id}
                vraag={q}
                nummer={i + 1}
                waarde={scores[q.id] ?? 0}
                onChange={(v) => setScores((prev) => ({ ...prev, [q.id]: v }))}
              />
            ))}

            <div>
              <label htmlFor="open_comment" className="block text-sm font-medium text-gray-500 mb-1">
                {form.questions.length + 1}. {open?.label ?? t('review.commentLabel')}
              </label>
              <p className="text-gray-900 mb-3">{open?.question ?? t('review.commentLabel')}</p>
              <textarea
                id="open_comment"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B1F3A] focus:border-[#0B1F3A]"
                placeholder={open?.placeholder ?? t('review.commentPlaceholder')}
                value={openComment}
                onChange={(e) => setOpenComment(e.target.value)}
                maxLength={2000}
              />
              <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                {open?.privacyNote ?? t('review.commentNote')}
              </p>
            </div>

            {error && <p className="text-red-600 text-sm" role="alert">{error}</p>}

            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? t('review.submitting') : t('review.submit')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
